// Timezone-independent date utilities pinned to Asia/Kolkata (IST, UTC+05:30)
import { formatDistanceStrict } from "date-fns";

const TIMEZONE = "Asia/Kolkata";

const storyDateFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIMEZONE,
  day: "2-digit",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23"
});

const storyDateOnlyFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIMEZONE,
  day: "numeric",
  month: "long",
  year: "numeric"
});

const isoPartsFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23"
});

const liveBlogTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIMEZONE,
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23"
});

const liveBlogDateFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIMEZONE,
  day: "2-digit",
  month: "short",
  year: "numeric"
});

/**
 * Formats a timestamp into Deccan Herald story publication/update date & time in IST (Asia/Kolkata).
 * Example: "07 October 2026, 13:05"
 *
 * @param {number|string|Date} timestamp - Epoch ms, ISO string, or Date object
 * @returns {string} Formatted date string in English, or empty string if invalid
 */
export const formatStoryDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const parts = storyDateFormatter.formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  return `${parts.day} ${parts.month} ${parts.year}, ${parts.hour}:${parts.minute}`;
};

/**
 * Backward-compatible alias for handleTimeFormat
 */
export const handleTimeFormat = formatStoryDate;

/**
 * Formats a timestamp into date-only format in IST (Asia/Kolkata).
 * Example: "7 October 2026"
 *
 * @param {number|string|Date} timestamp - Epoch ms, ISO string, or Date object
 * @returns {string} Formatted date string, or empty string if invalid
 */
export const formatStoryDateOnly = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const parts = storyDateOnlyFormatter.formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  return `${parts.day} ${parts.month} ${parts.year}`;
};

/**
 * Formats a timestamp into an unambiguous ISO-8601 string with +05:30 offset for SEO / JSON-LD structured data.
 * Example: "2026-10-07T13:05:21+05:30"
 *
 * @param {number|string|Date} timestamp - Epoch ms, ISO string, or Date object
 * @returns {string} ISO-8601 string with +05:30 offset, or empty string if invalid
 */
export const toISTIsoString = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const parts = isoPartsFormatter.formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}+05:30`;
};

/**
 * Backward-compatible schemaDate for SEO and AMP structured data tags.
 * Example: "2026-10-07T13:05:21+05:30"
 */
export const schemaDate = toISTIsoString;

/**
 * Formats a timestamp to "YYYY-MM-DD HH:mm" in IST (Asia/Kolkata) for DataLayer / Analytics.
 * Example: "2026-10-07 13:05"
 *
 * @param {number|string|Date} timestamp - Epoch ms, ISO string, or Date object
 * @returns {string} Formatted string or "NA" if invalid
 */
export const convertTimestampToYYYYMMDDHHMM = (timestamp) => {
  if (!timestamp) return "NA";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "NA";

  const parts = isoPartsFormatter.formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
};

/**
 * DataLayer convertTimestampToZTime returning IST ISO string with offset.
 */
export const convertTimestampToZTime = toISTIsoString;

/**
 * Formats a timestamp into Live Blog time in IST (Asia/Kolkata).
 * Example: "13:05"
 *
 * @param {number|string|Date} timestamp - Epoch ms, ISO string, or Date object
 * @returns {string} Formatted time string (24h), or empty string if invalid
 */
export const formatLiveBlogTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  return liveBlogTimeFormatter.format(date);
};

/**
 * Formats a timestamp into Live Blog date in IST (Asia/Kolkata).
 * Example: "07 Oct 2026"
 *
 * @param {number|string|Date} timestamp - Epoch ms, ISO string, or Date object
 * @returns {string} Formatted date string, or empty string if invalid
 */
export const formatLiveBlogDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  return liveBlogDateFormatter.format(date);
};

/**
 * Extracts the canonical publication/update timestamp from a story object or raw timestamp.
 * Canonical precedence: last-published-at -> published-at -> first-published-at
 *
 * @param {object|number|string|Date} storyOrTimestamp
 * @returns {number|string|Date|null}
 */
export const getStoryTimestamp = (storyOrTimestamp) => {
  if (!storyOrTimestamp) return null;
  if (
    typeof storyOrTimestamp === "number" ||
    typeof storyOrTimestamp === "string" ||
    storyOrTimestamp instanceof Date
  ) {
    return storyOrTimestamp;
  }
  return (
    storyOrTimestamp["last-published-at"] ||
    storyOrTimestamp["published-at"] ||
    storyOrTimestamp["first-published-at"] ||
    null
  );
};

/**
 * Computes structured relative time information for a given timestamp.
 * Standardized across all story cards and listings using round-to-nearest policy.
 *
 * @param {number|string|Date} timestamp
 * @param {object} [options]
 * @param {Date|number} [options.baseDate] - Reference base date (defaults to current time)
 * @returns {object|null}
 */
export const getRelativeTimeInfo = (timestamp, options = {}) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return null;

  const baseDate = options.baseDate ? new Date(options.baseDate) : new Date();
  if (isNaN(baseDate.getTime())) return null;

  const diffMs = Math.max(0, baseDate.getTime() - date.getTime());
  const hours = diffMs / (1000 * 60 * 60);

  // Stories 24 hours or older switch to absolute IST date formatting
  if (hours >= 24) {
    const formattedStoryDate = formatStoryDate(date);
    const formattedDateOnly = formatStoryDateOnly(date);
    return {
      isOlderThan24Hours: true,
      isJustNow: false,
      count: null,
      unit: "day",
      desktopUnit: "",
      mobileUnit: "",
      formattedStoryDate,
      formattedDateOnly,
      fullText: `${formattedStoryDate} IST`
    };
  }

  // Under 60 seconds
  if (diffMs < 60 * 1000) {
    return {
      isOlderThan24Hours: false,
      isJustNow: true,
      count: null,
      unit: "minute",
      desktopUnit: "Just now",
      mobileUnit: "Just now",
      fullText: "Just now",
      formattedStoryDate: formatStoryDate(date),
      formattedDateOnly: formatStoryDateOnly(date)
    };
  }

  const timeDistance = formatDistanceStrict(date, baseDate, { roundingMethod: "round" });
  const [count, rawUnit] = timeDistance.split(" ");
  const isHour = rawUnit.startsWith("hour");

  const desktopUnit = isHour
    ? count === "1"
      ? "hour ago"
      : "hours ago"
    : count === "1"
    ? "minute ago"
    : "minutes ago";

  const mobileUnit = isHour ? (count === "1" ? "hr. ago" : "hrs. ago") : count === "1" ? "min. ago" : "mins. ago";

  return {
    isOlderThan24Hours: false,
    isJustNow: false,
    count,
    unit: isHour ? (count === "1" ? "hour" : "hours") : count === "1" ? "minute" : "minutes",
    desktopUnit,
    mobileUnit,
    fullText: `${count} ${desktopUnit}`,
    formattedStoryDate: formatStoryDate(date),
    formattedDateOnly: formatStoryDateOnly(date)
  };
};

/**
 * Returns formatted relative or absolute date string for story cards.
 * If < 24h: returns relative time (e.g. "4 hours ago", "1 hour ago", "Just now").
 * If >= 24h: returns absolute IST date (e.g. "07 October 2026, 12:10 IST").
 *
 * @param {object|number|string|Date} storyOrTimestamp
 * @param {object} [options]
 * @returns {string}
 */
export const formatRelativeTime = (storyOrTimestamp, options = {}) => {
  const timestamp = getStoryTimestamp(storyOrTimestamp);
  if (!timestamp) return "";
  const info = getRelativeTimeInfo(timestamp, options);
  if (!info) return "";
  return info.fullText;
};
